import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { SplashAuthShell } from './SplashAuthShell';
import { formatPhone } from '../lib/format';

type Step = 'pin' | 'form';

const PIN_API = '/api/verify-pin';

export function Register() {
  const { signUpLessee, configured } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState<Step>('pin');
  const [pin, setPin] = useState('');
  const [pinErr, setPinErr] = useState<string | null>(null);
  const [pinBusy, setPinBusy] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [formErr, setFormErr] = useState<string | null>(null);
  const [formBusy, setFormBusy] = useState(false);

  async function onPin(e: React.FormEvent) {
    e.preventDefault();
    setPinErr(null);
    setPinBusy(true);
    try {
      const res = await fetch(PIN_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setPinErr(data.message || 'Invalid PIN.');
        return;
      }
      setStep('form');
    } catch {
      setPinErr('Could not reach the server. Try again in a moment.');
    } finally {
      setPinBusy(false);
    }
  }

  function passwordOk(p: string) {
    return p.length >= 10 && /[A-Za-z]/.test(p) && /\d/.test(p);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormErr(null);
    if (!firstName.trim() || !lastName.trim()) return setFormErr('Enter your full legal name.');
    if (!phone.trim()) return setFormErr('Enter your phone number.');
    if (!passwordOk(password)) {
      return setFormErr('Password must be at least 10 characters with letters and a number.');
    }
    if (password !== password2) return setFormErr('Passwords do not match.');

    setFormBusy(true);
    try {
      await signUpLessee({ email, password, firstName, lastName, phone });
      nav('/pending', { replace: true });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not create account.';
      setFormErr(msg.replace('Firebase: ', ''));
    } finally {
      setFormBusy(false);
    }
  }

  return (
    <SplashAuthShell
      title={step === 'pin' ? 'Register' : 'Create your account'}
      subtitle={
        step === 'pin'
          ? 'Enter the PIN provided by AJM Aviation to begin registration.'
          : 'You’ll be reviewed by the owner before gaining access.'
      }
    >
      {!configured && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong className="font-semibold">Setup pending.</strong> Firebase credentials haven’t been added yet. See <code>SETUP.md</code>.
        </div>
      )}
      {step === 'pin' ? (
        <form onSubmit={onPin} className="space-y-4">
          <div>
            <label className="label" htmlFor="pin">Registration PIN</label>
            <input
              id="pin"
              type="text"
              autoComplete="off"
              autoFocus
              required
              className="input tracking-widest font-mono"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
          </div>
          {pinErr && (
            <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {pinErr}
            </div>
          )}
          <button type="submit" disabled={pinBusy} className="btn-primary w-full">
            {pinBusy ? 'Verifying…' : 'Continue'}
          </button>
          <div className="text-sm text-center">
            <Link to="/login" className="text-navy-700 hover:text-navy-900 underline">
              Back to sign in
            </Link>
          </div>
        </form>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="fn">First name</label>
              <input id="fn" className="input" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="ln">Last name</label>
              <input id="ln" className="input" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="em">Email</label>
            <input id="em" type="email" autoComplete="email" required className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="ph">Phone</label>
            <input id="ph" type="tel" autoComplete="tel" required className="input" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} />
          </div>
          <div>
            <label className="label" htmlFor="pw">Password</label>
            <input id="pw" type="password" autoComplete="new-password" required className="input" value={password} onChange={(e) => setPassword(e.target.value)} />
            <p className="text-xs text-ink-muted mt-1">At least 10 characters, letters and a number.</p>
          </div>
          <div>
            <label className="label" htmlFor="pw2">Confirm password</label>
            <input id="pw2" type="password" autoComplete="new-password" required className="input" value={password2} onChange={(e) => setPassword2(e.target.value)} />
          </div>
          {formErr && (
            <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {formErr}
            </div>
          )}
          <button type="submit" disabled={formBusy || !configured} className="btn-primary w-full">
            {formBusy ? 'Creating account…' : 'Create account'}
          </button>
          <div className="text-sm text-center">
            <Link to="/login" className="text-navy-700 hover:text-navy-900 underline">
              Already have an account? Sign in
            </Link>
          </div>
        </form>
      )}
    </SplashAuthShell>
  );
}
